/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { createSelector } from '@reduxjs/toolkit';
import type { SoundingsCollection, SourceSelection } from '@opengeoweb/shared';
import { selectorMemoizationOptions } from '@opengeoweb/store';
import { initialSoundingsViewState } from './reducer';
import type {
  SoundingsModuleState,
  SoundingsViewState,
  SourceType,
} from './types';
import { isRegisteredViewId } from './utils';
import { DEFAULT_PROFILE_DATA_PREFERENCES } from '../utils/profileDataPreferences';
import type { SoundingsProfileDataPreferences } from '../utils/profileDataPreferences';

const emptySoundingsCollections: SoundingsCollection[] = [];

/**
 * Returns the state for a specific soundings panel. Since panels are added
 * dynamically, the requested panel may not exist yet — in that case the
 * default panel state is returned. All panel-specific selectors below
 * build on top of this.
 */
const getSoundingsViewState = (
  store: SoundingsModuleState,
  viewId?: string,
): SoundingsViewState => {
  const soundingsState = store?.soundings;
  if (!viewId || !isRegisteredViewId(soundingsState, viewId)) {
    return initialSoundingsViewState;
  }
  const panelStore: SoundingsViewState | undefined =
    soundingsState?.byId[viewId];
  return panelStore ?? initialSoundingsViewState;
};

export const getSoundingsCollections = (
  store: SoundingsModuleState,
): SoundingsCollection[] =>
  store?.soundings?.soundingsCollections || emptySoundingsCollections;

export const getSourceSelections = createSelector(
  getSoundingsViewState,
  (store: SoundingsViewState): Record<string, SourceSelection> =>
    store.selectedSources,
  selectorMemoizationOptions,
);

export const getLocationSelections = createSelector(
  getSoundingsViewState,
  (store: SoundingsViewState) => store.selectedLocations,
  selectorMemoizationOptions,
);

export const getSelectedTime = createSelector(
  getSoundingsViewState,
  (store: SoundingsViewState): string =>
    store.selectedTime ?? new Date().toISOString(),
  selectorMemoizationOptions,
);

export const getDiagramType = createSelector(
  getSoundingsViewState,
  (store: SoundingsViewState): 'SKEWT' | 'BIJVOET' | 'EMAGRAM' =>
    store.diagramType ?? 'SKEWT',
  selectorMemoizationOptions,
);

export const getPanelIsOpen = createSelector(
  [
    (
      store: SoundingsModuleState,
      viewId: string,
      _panelId: string,
    ): SoundingsViewState => getSoundingsViewState(store, viewId),
    (_store: SoundingsModuleState, _viewId: string, panelId: string): string =>
      panelId,
  ],
  (store: SoundingsViewState, panelId: string): boolean | undefined =>
    store.soundingPanels?.entities[panelId]?.isOpen,
  selectorMemoizationOptions,
);

export const getPanelCustomWidth = createSelector(
  [
    (
      store: SoundingsModuleState,
      viewId: string,
      _panelId: string,
    ): SoundingsViewState => getSoundingsViewState(store, viewId),
    (_store: SoundingsModuleState, _viewId: string, panelId: string): string =>
      panelId,
  ],
  (store: SoundingsViewState, panelId: string): number | undefined =>
    store.soundingPanels?.entities[panelId]?.customWidth,
  selectorMemoizationOptions,
);

export const getShouldHideLocationMarkers = createSelector(
  getSoundingsViewState,
  (store: SoundingsViewState): boolean =>
    store.shouldHideLocationMarkers ?? false,
  selectorMemoizationOptions,
);

export const getProfileDataPreferences = createSelector(
  getSoundingsViewState,
  (store: SoundingsViewState): SoundingsProfileDataPreferences =>
    store.profileDataPreferences ?? DEFAULT_PROFILE_DATA_PREFERENCES,
  selectorMemoizationOptions,
);

export const getSelectedSources = createSelector(
  getSoundingsViewState,
  (store: SoundingsViewState): SourceType[] => {
    const sources = store.selectedSources;
    const activeSources = Object.keys(sources).reduce<SourceType[]>(
      (list, key) => {
        const value = sources[key];
        if (value.isChecked === true) {
          return list.concat({
            collectionId: key,
            url: value.url ?? '',
            instanceId: value.instanceId,
            verticalRange: value.verticalRange,
            parameterMappings: value.parameterMappings,
            windSpeedUnit: value.windSpeedUnit ?? 'ms',
            name: value.name ?? '',
          });
        }
        return list;
      },
      [],
    );
    return activeSources;
  },
  selectorMemoizationOptions,
);

export const getSelectedSourcesAmount = createSelector(
  getSelectedSources,
  (activeSources: SourceType[]): number => {
    return activeSources.length;
  },
  selectorMemoizationOptions,
);

export const getSelectedSourcesIds = createSelector(
  getSelectedSources,
  (activeSources: SourceType[]): string[] => {
    return activeSources.map((source) => source.collectionId);
  },
  selectorMemoizationOptions,
);

export const getSoundingsPreset = createSelector(
  getSoundingsViewState,
  (soundingsStore: SoundingsViewState): SoundingsViewState => {
    return { ...soundingsStore };
  },
  selectorMemoizationOptions,
);

const EMPTY_SOUDINGS_PANELS = { order: [], entities: {} };

export const getSoundingPanelsInOrder = createSelector(
  getSoundingsViewState,
  (store: SoundingsViewState) => {
    const { order = [], entities = {} } =
      store.soundingPanels || EMPTY_SOUDINGS_PANELS;
    return order.map((id) => entities[id]);
  },
  selectorMemoizationOptions,
);
